import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { submitQA, setProgramDuration, setProgramCost } from "../redux/actions";
import useIntersectionObserver from "../hooks/useIntersectionObserver";
import styled from "styled-components";
import Card from "../components/Base/Card";
import { QuestionnaireTitle } from "../components/Base/QuestionnaireTitle";
import GoalIcon from "../components/GoalIcon";
import InputLabelAbove from "../components/Base/InputLabelAbove";
import { validateField } from "../utils";
import { analytics } from "../analytics";

const Wrapper = styled.div`
  width: 100%;
  max-width: 1076px;

  .questionnaireHeader {
    display: flex;
  }

  .icon {
    display: inline-block;
    width: 105px;
    height: 105px;
    aspect-ratio: 1;
    background-color: white;

    svg {
      height: 100%;
      width: 100%;
    }
  }

  .header {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    margin-left: 30px;
    padding: 10px 0;
  }

  .goalType {
    text-align: left;
    text-transform: uppercase;
    font-weight: 600;
  }

  @media (max-width: 480px) {
    .formContainer {
      margin-top: 5%;
    }
  }

  @media (max-width: 480px) {
    .icon {
      display: none;
    }

    .header {
      margin-left: 0px;
    }
  }

  @media (max-width: 480px) {
    padding: 0% 5%;
  }

  @media (max-width: 1075px) {
    padding: 0% 5%;
  }
`;

// Helper functions
const applyContraint = (min, max, constraint, connectedFieldVal) => {
  switch (constraint) {
    case "less than or equal to": {
      return {
        min,
        max: !connectedFieldVal ? max : Math.min(max, connectedFieldVal),
      };
    }
    case "less than": {
      return {
        min,
        max: Number(
          !connectedFieldVal ? max : Math.min(max, connectedFieldVal) - 1
        ),
      };
    }
    case "greater than or equal to": {
      return { min: Math.max(min, connectedFieldVal), max };
    }
    default: {
      return { min, max };
    }
  }
};

const validateFieldWithConstraint = (tmpMin, tmpMax, consInput, ints) => {
  if (!consInput) return;

  let minMax = {
    min: tmpMin,
    max: tmpMax,
  };

  const cnstFieldValue = ints.find(
    (inVls) => inVls.id === consInput.connectedQuestionId
  );
  if (!cnstFieldValue) return minMax;

  minMax = applyContraint(
    tmpMin,
    tmpMax,
    consInput.constraint,
    Number(cnstFieldValue.answer)
  );
  return minMax;
};

const isProgramDurationQuestion = (goalType, questionText) =>
  goalType === "Education" &&
  questionText === "How many years will it take to complete the program?";

const isProgramCostQuestion = (goalType, questionText) =>
  goalType === "Education" &&
  questionText === "What does the program cost annually?";

const isStartingInvestment = (goalType, questionText) =>
  goalType === "Education" &&
  questionText === "What is your starting investment amount?";

// Component
const FreeNumberInput = ({ stepProps, questions }) => {
  const dispatch = useDispatch();
  const { goal, programDuration, programCost } = useSelector(
    (state) => state.user
  );
  const { setNextBtnDisabled, setNextAction } = stepProps;
  const ref = useRef(null);
  const entry = useIntersectionObserver(ref, {});
  // Use the questions from backend and add answer property
  const [inputQuestions, setInputQuestions] = useState(
    questions.map((question) => ({ ...question, answer: "" }))
  );
  const [errors, setErrors] = useState(
    questions.map((question) => ({ inputId: question.id, errorMessage: "" }))
  );

  const isVisible = !!entry?.isIntersecting;

  const submitData = useCallback(async () => {
    const data = {
      goalId: goal.id,
      userAnswers: inputQuestions.map(({ id, answer }) => {
        return {
          questionId: id,
          answer,
        };
      }),
    };
    const response = await dispatch(submitQA(data));

    inputQuestions.forEach((data) => {
      analytics.track("Question Answered", {
        goalId: goal?.id || "",
        goalType: goal?.type || "",
        questionScreen: "Lets get started",
        questionText: data?.text || "",
        questionInput: data?.answer || "",
      });
    });

    return response;
  }, [dispatch, goal.id, inputQuestions]);

  useEffect(() => {
    if (isVisible && inputQuestions) {
      // Disable Next button if some input is empty
      // Or there are errors
      const answersFilledIn = inputQuestions.every(
        (input) => input.answer !== ""
      );
      const errorsExist = errors.some((err) => err.errorMessage !== "");
      setNextBtnDisabled(!answersFilledIn || errorsExist);
      setNextAction((prevAction) => (prevAction = submitData));
    }
  }, [
    isVisible,
    inputQuestions,
    errors,
    setNextBtnDisabled,
    setNextAction,
    submitData,
  ]);

  const handleChange = useCallback(
    (e, qDetails, ints, pDuration, pCost) => {
      if (isNaN(e.target.value)) return;
      const { id, minValue, maxValue, text, connectedInputs } = qDetails;

      if (isProgramCostQuestion(goal.type, text)) {
        dispatch(setProgramCost(Number(e.target.value)));
      }

      if (isProgramDurationQuestion(goal.type, text)) {
        dispatch(setProgramDuration(Number(e.target.value)));
      }

      let minMax = {
        min: minValue,
        max: maxValue,
      };

      if (connectedInputs.length) {
        connectedInputs.forEach((inpt) => {
          const tmpMinMax = validateFieldWithConstraint(
            minMax.min,
            minMax.max,
            inpt,
            ints
          );
          if (tmpMinMax.min > (minMax.min || 0)) {
            minMax.min = tmpMinMax.min;
          }
          if (tmpMinMax.max < (minMax.max || 9999999999)) {
            minMax.max = tmpMinMax.max;
          }
        });
      }

      if (isStartingInvestment(goal.type, text)) {
        minMax.max = pDuration * pCost;
      }

      const errMsg = validateField(minMax.min, minMax.max, e.target.value);

      setInputQuestions((prevVal) =>
        prevVal.map((val) =>
          val.id === id ? { ...val, answer: e.target.value } : val
        )
      );

      setErrors((prevErrs) =>
        prevErrs.map((err) =>
          err.inputId === id ? { ...err, errorMessage: errMsg } : err
        )
      );
    },
    [setInputQuestions, dispatch, goal.type]
  );
  return (
    <Wrapper ref={ref}>
      <div className="questionnaireHeader">
        <Card className="icon">
          <GoalIcon type={goal.type} />
        </Card>
        <div className="header">
          <span className="goalType">{goal.type}</span>
          <QuestionnaireTitle>{"Let's get started."}</QuestionnaireTitle>
        </div>
      </div>
      <div className="formContainer">
        <form action="">
          {inputQuestions?.map((qDetails, i) => (
            <InputLabelAbove
              index={i}
              allQuestions={inputQuestions}
              key={qDetails.id}
              id={qDetails.id}
              type="text"
              placeholder={qDetails.placeholder}
              label={qDetails.text}
              name={qDetails.id}
              value={
                inputQuestions.find((inptQst) => inptQst.id === qDetails.id)
                  ?.answer || ""
              }
              onChange={(e) =>
                handleChange(
                  e,
                  qDetails,
                  inputQuestions,
                  programDuration,
                  programCost
                )
              }
              error={errors.find((errItem) => errItem.inputId === qDetails.id)}
            />
          ))}
        </form>
      </div>
    </Wrapper>
  );
};
export default FreeNumberInput;
